import { Fragment, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useHistory, Link } from 'react-router-dom';
import cn from 'clsx';

import { useAuth } from '@hooks/Auth';
import { CrossIcon } from '@components/elements/Icons';
import { CompanyRole, UserType } from '@sdk/API';

export interface SideMenuProps {
  open?: boolean;
  onClose: () => void;
}

const itemCn =
  'font-display tracking-tighter font-bold text-4xl sm:text-5xl text-opacity-90 hover:text-opacity-100 active:bg-green hover:bg-green w-full p-2 hover:text-yellow active:text-yellow text-center';

const SideMenu: React.FC<SideMenuProps> = ({ open = false, onClose }) => {
  const { user, dbUser, logout, company } = useAuth();
  const history = useHistory<any>();

  const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

  const onLogout = useCallback(async () => {
    await logout();
    history.push('/');
    onClose();
  }, [logout, history, onClose]);

  const isLogin = !!user;
  const isAdmin =
    dbUser?.type === UserType.ADMINISTRATOR ||
    company?.role === CompanyRole.ADMIN ||
    company?.role === CompanyRole.REPRESENTATIVE;
  return (
    <Transition show={open}>
      {/*
      Don't forget to add `static` and `open` to your Dialog!
    */}
      <Dialog
        static
        open={open}
        onClose={onClose}
        initialFocus={cancelButtonRef}
        className="fixed inset-0 z-50 overflow-y-none"
      >
        <div className="static w-screen h-screen">
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear delay-100 duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay
              className="fixed inset-0 bg-white"
              onClick={onClose}
            />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out delay-50 duration-200 transform"
            enterFrom="translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="transition ease-in-out duration-200 transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="-translate-y-full opacity-0"
          >
            <div className="absolute right-0 z-20 w-12 h-12 p-2 mt-2 mr-2 rounded-lg hover:bg-black hover:bg-opacity-5">
              <button
                ref={cancelButtonRef}
                className="unstyled-btn"
                onClick={onClose}
              >
                <CrossIcon className="w-full h-full" />
              </button>
            </div>
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out delay-100 duration-300 transform"
            enterFrom="translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="-translate-y-full opacity-0"
          >
            <div className="absolute left-0 z-10 flex flex-col items-center w-full h-full">
              <Dialog.Title className="py-2 mt-2 text-3xl font-bold font-display">
              MegaX World
              </Dialog.Title>

              <div className="flex flex-col items-center justify-center w-full h-full -mt-12">
                <a
                  href="https://megax.world/"
                  onClick={onClose}
                  className={cn(itemCn, 'text-blue')}
                >
                  Home
                </a>
                {isLogin ? (
                  <>
                    <Link
                      to="/feed"
                      onClick={onClose}
                      className={cn(itemCn, 'text-magenta')}
                    >
                      Your Feed
                    </Link>

                    {isAdmin && (
                      <>
                        <Link
                          to="/poll/create"
                          onClick={onClose}
                          className={cn(itemCn, 'text-purple3')}
                        >
                          Create Poll
                        </Link>

                        <Link
                          to="/admin/dashboard"
                          onClick={onClose}
                          className={cn(itemCn, 'text-blue2')}
                        >
                          Company Dashboard
                        </Link>

                        <Link
                          to="/admin/polls"
                          onClick={onClose}
                          className={cn(itemCn, 'text-blue2')}
                        >
                          Manage Company Polls
                        </Link>

                        <Link
                          to="/admin/members"
                          onClick={onClose}
                          className={cn(itemCn, 'text-purple2')}
                        >
                          Company Member Approval
                        </Link>

                        {dbUser?.type === UserType.ADMINISTRATOR ||
                          (company?.role === CompanyRole.ADMIN && (
                            <Link
                              to="/admin/representatives"
                              onClick={onClose}
                              className={cn(itemCn, 'text-purple2')}
                            >
                              Assign Company Representative
                            </Link>
                          ))}
                        <Link
                          to="/admin/proxy"
                          onClick={onClose}
                          className={cn(itemCn, 'text-purple3')}
                        >
                          Assign Company Proxy
                        </Link>
                        <Link
                          to="/admin/votes"
                          onClick={onClose}
                          className={cn(itemCn, 'text-purple4')}
                        >
                          Withdraw Company Poll Votes
                        </Link>

                        <Link
                          to="/company/settings"
                          onClick={onClose}
                          className={cn(itemCn, 'text-purple3')}
                        >
                          Company Settings
                        </Link>
                      </>
                    )}

                    <div
                      role="button"
                      className={cn(itemCn, 'text-black cursor-pointer')}
                      onClick={onLogout}
                    >
                      Log Out
                    </div>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={onClose}
                      className={cn(itemCn, 'text-magenta')}
                    >
                      Log In
                    </Link>
                    <Link
                      to="/signup"
                      onClick={onClose}
                      className={cn(itemCn, 'text-salmon')}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SideMenu;
